import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type * as C from "@zooi/contracts";
import { type FormEvent, useState } from "react";
import { api, q } from "./api.ts";
import { notify } from "./feedback.ts";
import { dateTime } from "./format.ts";
import { DISPATCH_LABELS as L, SETTINGS_LABELS as S } from "./labels.ts";
import { Button, CopyButton, ErrorText, Field, Section } from "./ui.tsx";
import { PropertyList, PropertyRow } from "./ui/props.tsx";
import { Select } from "./ui/select.tsx";
import { Skeleton } from "./ui/skeleton.tsx";
import { StatusIcon } from "./ui/status.tsx";

// Settings › Agent and runs › Dispatch (design/architecture.md §2.5, decided
// 2026-11-03; docs/design-system.md §0.4g): where zooi sends the product's
// run requests. A signed webhook, whose secret is shown once, or a GitHub
// Actions repository with a token. Delivery only: the inbox stays.

/** The plugin's guide to receiving a dispatch, in the public plugin repository. */
export const DISPATCH_GUIDE = "https://github.com/ZooiAI/zooi-plugin/blob/main/ACTIONS.md";

const iconOf = (target: C.DispatchTarget | null): "not_started" | "approved" | "done" | "stopped" => (!target ? "not_started" : !target.last ? "approved" : target.last.ok ? "done" : "stopped");

export function DispatchSettings({ product }: { product: C.Product }) {
  const client = useQueryClient();
  const view = useQuery(q.dispatchTarget(product.id));
  const [kind, setKind] = useState<C.DispatchTarget["kind"]>("webhook");
  const [url, setUrl] = useState("");
  const [repository, setRepository] = useState("");
  const [token, setToken] = useState("");
  const [secret, setSecret] = useState<string | null>(null);
  const base = `/api/products/${encodeURIComponent(product.id)}/dispatch`;
  const refresh = () => client.invalidateQueries({ queryKey: q.dispatchTarget(product.id).queryKey });
  const save = useMutation({
    mutationFn: () => api.put<C.DispatchTargetSaved>(base, kind === "webhook" ? { kind, url: url.trim() } : { kind, repository: repository.trim(), token: token.trim() }),
    onSuccess: async (saved) => { setSecret(saved.secret); setToken(""); await refresh(); notify.success(S.saved); },
  });
  const remove = useMutation({ mutationFn: () => api.del<C.DispatchTargetView>(base), onSuccess: async () => { setSecret(null); await refresh(); } });
  const test = useMutation({ mutationFn: () => api.post<C.DispatchResult>(`${base}/test`), onSuccess: async (result) => { await refresh(); if (result.ok) notify.success(L.tested(true)); else notify.error(L.tested(false)); } });
  const target = view.data?.target ?? null;
  const ready = kind === "webhook" ? url.trim().length > 0 : repository.trim().length > 0 && token.trim().length > 0;
  const submit = (event: FormEvent) => { event.preventDefault(); if (ready) save.mutate(); };
  return <Section id="dispatch" title={L.title} note={L.note}>
    {view.error ? <ErrorText error={view.error} /> : view.isLoading ? <Skeleton shape="list" rows={1} /> : <PropertyList>
      <PropertyRow icon={<StatusIcon kind={iconOf(target)} label="" />} label={L.target}>{target ? (target.kind === "webhook" ? L.webhookAt(target.url ?? "") : L.actionsAt(target.repository ?? "")) : L.none}</PropertyRow>
      {target && <PropertyRow label={L.last}>{target.last ? (target.last.ok ? L.lastOk(dateTime(target.last.at)) : L.lastFailed(dateTime(target.last.at), target.last.error ?? "")) : L.never}</PropertyRow>}
    </PropertyList>}
    {secret && <div className="z-boxed z-flow"><p className="z-small">{L.secretNote}</p><p><code>{secret}</code> <CopyButton text={secret} /></p></div>}
    <form className="z-flow" onSubmit={submit}>
      <Field label={L.kind}><Select variant="form" label={L.kind} value={kind} onValueChange={(nextValue) => setKind(nextValue as C.DispatchTarget["kind"])} options={[{ value: "webhook", label: L.webhook }, { value: "github_actions", label: L.actions }]} /></Field>
      {kind === "webhook"
        ? <Field label={L.url} help={L.urlHelp}><input className="z-input" type="url" inputMode="url" value={url} onChange={(event) => setUrl(event.target.value)} maxLength={500} disabled={save.isPending} /></Field>
        : <>
          <Field label={L.repository} help={L.repositoryHelp}><input className="z-input" value={repository} onChange={(event) => setRepository(event.target.value)} placeholder="owner/name" maxLength={200} disabled={save.isPending} /></Field>
          <Field label={L.token} help={L.tokenHelp}><input className="z-input" type="password" autoComplete="off" value={token} onChange={(event) => setToken(event.target.value)} maxLength={400} disabled={save.isPending} /></Field>
        </>}
      <div className="z-dialog-actions">
        {target && <Button tone="danger" busy={remove.isPending} disabled={save.isPending || test.isPending} onClick={() => remove.mutate()}>{L.remove}</Button>}
        {target && <Button busy={test.isPending} disabled={save.isPending || remove.isPending} onClick={() => test.mutate()}>{L.test}</Button>}
        <Button type="submit" tone="primary" busy={save.isPending} disabled={!ready}>{target ? L.replace : L.save}</Button>
      </div>
      <ErrorText error={save.error ?? remove.error ?? test.error} />
    </form>
    <p className="z-small"><a className="z-link" href={DISPATCH_GUIDE} target="_blank" rel="noreferrer">{L.guide}</a></p>
  </Section>;
}
